import { exhaustiveCheck } from "ts-exhaustive-check";
import type { Dispatch, EffectManager } from "@typescript-tea/core";
import { type HttpFetchCmd, type HttpResponseType, mapCmd } from "./http-fetch-cmd";
import { home } from "./home";

export type HttpState = "idle" | "waiting" | "error";

export function createEffectManager<ActionApp>(onHttpStateChanged: (httpState: HttpState) => ActionApp): EffectManager {
  return {
    home,
    mapCmd,
    mapSub: (_, _sub) => {
      throw new Error("This effect manager has no subs.");
    },
    setup: () => () => undefined,
    onEffects: (
      dispatchApp: Dispatch<ActionApp>,
      dispatchSelf: Dispatch<never>,
      cmds: readonly HttpFetchCmd<ActionApp, unknown>[],
      subs: readonly never[],
      state: State = init()
    ) => onEffects(dispatchApp, dispatchSelf, cmds, subs, state || init(), onHttpStateChanged),
    onSelfAction: () => ({}),
  };
}

// -- STATE

interface State {}

function init(): readonly [State] {
  return [{}];
}

// -- COMMANDS

// -- SUBSCRIPTIONS

// -- MANAGER

let pendingRequests = 0;

function onEffects<ActionApp>(
  dispatchApp: Dispatch<ActionApp>,
  _dispatchSelf: Dispatch<never>,
  cmds: readonly HttpFetchCmd<ActionApp, unknown>[],
  _subs: readonly never[],
  state: State,
  onHttpStateChanged: (httpState: HttpState) => ActionApp
): State {
  // TODO: decPendingRequets, handle exceptions

  const decPendingRequets = (): void => {
    pendingRequests = Math.max(0, pendingRequests - 1);
    if (pendingRequests === 0) {
      dispatchApp(onHttpStateChanged("idle"));
    }
  };
  const incPendingRequets = (): void => {
    pendingRequests++;
    if (pendingRequests === 1) {
      dispatchApp(onHttpStateChanged("waiting"));
    }
  };
  const onError = (): void => {
    dispatchApp(onHttpStateChanged("error"));
  };
  for (const cmd of cmds) {
    switch (cmd.type) {
      case "FetchOne": {
        incPendingRequets();
        httpFetch(cmd.headers, cmd.url, cmd.responseType, () => (cmd.onError ? cmd.onError() : onError()))
          .catch(() => (cmd.onError ? cmd.onError() : onError()))
          .then((data) => {
            cmd.onSuccess && dispatchApp(cmd.onSuccess(data));
          })
          .finally(decPendingRequets);
        break;
      }
      case "FetchMultiple": {
        incPendingRequets();
        Promise.all(cmd.urls.map((url) => httpFetch(cmd.headers, url, cmd.responseType, onError)))
          .catch(onError)
          .then((responsesData) => {
            if (typeof responsesData === "undefined") {
              return;
            }
            cmd.onSuccess && dispatchApp(cmd.onSuccess(responsesData));
          })
          .finally(decPendingRequets);
        break;
      }
      case "Post": {
        incPendingRequets();
        fetch(cmd.url, {
          method: "POST",
          headers: { "Content-Type": cmd.contentType, ...cmd.headers },
          body: cmd.body,
        })
          .catch(onError)
          .then((response) => {
            if (typeof response === "undefined") {
              throw new Error("Http fetch manager crashed");
            }
            if (response.status >= 400) {
              onError();
            }
            return handleResponse(response, cmd.responseType);
          })
          .then((result) => cmd.onSuccess && dispatchApp(cmd.onSuccess(result)))
          .finally(decPendingRequets);
        break;
      }
      case "Put": {
        incPendingRequets();
        fetch(cmd.url, {
          method: "PUT",
          headers: { "Content-Type": cmd.contentType, ...cmd.headers },
          body: cmd.body,
        })
          .catch(onError)
          .then((response) => {
            if (typeof response === "undefined") {
              return;
            }
            if (response.status >= 400) {
              onError();
            }
          })
          .finally(decPendingRequets);
        break;
      }
      case "Delete": {
        incPendingRequets();
        fetch(cmd.url, {
          method: "DELETE",
          headers: { "Content-Type": cmd.contentType, ...cmd.headers },
          body: cmd.body,
        })
          .catch(onError)
          .then((response) => {
            if (typeof response === "undefined") {
              return;
            }
            if (response.status >= 400) {
              onError();
            }
          })
          .finally(decPendingRequets);
        break;
      }
      default: {
        exhaustiveCheck(cmd, true);
      }
    }
  }
  return [state];
}

async function httpFetch(
  headers: { readonly [header: string]: string },
  url: string,
  responseType: HttpResponseType,
  onError: () => void
  // biome-ignore lint/suspicious/noExplicitAny: the response body is decoded from json/text at runtime
): Promise<any> {
  const response = await fetch(url, {
    headers: headers,
  });
  if (response.status >= 400) {
    onError();
  }
  return handleResponse(response, responseType);
}

// biome-ignore lint/suspicious/noExplicitAny: the response body is decoded from json/text at runtime
async function handleResponse(response: Response, responseType: HttpResponseType): Promise<any> {
  switch (responseType) {
    case "json": {
      return response.json();
    }
    case "text": {
      return response.text();
    }
    case "blob": {
      return new Uint8Array(await response.arrayBuffer());
    }
    case "none": {
      return undefined;
    }
    default: {
      return exhaustiveCheck(responseType, true);
    }
  }
}
